import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Globe, Edit, ExternalLink, Trash2, MoreVertical, Search, BarChart3 } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  Input,
  Badge,
  Skeleton,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useToast,
} from '@mawkaa/ui';
import { useSitesStore } from '@/stores/sitesStore';
import type { Site } from '@mawkaa/types';

export function SitesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { sites, isLoading, fetchSites, deleteSite } = useSitesStore();
  const [search, setSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState<Site | null>(null);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const filteredSites = sites.filter((site) =>
    site.name.toLowerCase().includes(search.toLowerCase()) ||
    site.subdomain.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async () => {
    if (!siteToDelete) return;

    try {
      await deleteSite(siteToDelete.id);
      toast({ title: 'Site supprimé', description: 'Le site a été supprimé avec succès.' });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le site.',
        variant: 'destructive',
      });
    } finally {
      setDeleteDialogOpen(false);
      setSiteToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Mes sites</h1>
          <p className="text-muted-foreground mt-1">
            Gérez tous vos sites web
          </p>
        </div>
        <Button onClick={() => navigate('/templates')}>
          <Plus className="w-4 h-4 mr-2" />
          Nouveau site
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Rechercher un site..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Sites list */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-4 w-2/3 mb-2" />
                <Skeleton className="h-3 w-1/2 mb-4" />
                <Skeleton className="h-8 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredSites.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredSites.map((site) => (
            <Card key={site.id} className="group">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate">{site.name}</h3>
                    <p className="text-sm text-muted-foreground truncate">
                      {site.subdomain}.mawkaa.tn
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/builder/${site.id}`)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Modifier
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/dashboard/analytics/${site.id}`)}>
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Analytics
                      </DropdownMenuItem>
                      {site.status === 'published' && (
                        <DropdownMenuItem
                          onClick={() => window.open(`https://${site.subdomain}.mawkaa.tn`, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Voir le site
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => {
                          setSiteToDelete(site);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="flex items-center justify-between">
                  <Badge variant={site.status === 'published' ? 'success' : 'secondary'}>
                    {site.status === 'published' ? 'Publié' : 'Brouillon'}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    Modifié le {new Date(site.updatedAt).toLocaleDateString('fr-FR')}
                  </span>
                </div>

                <Button
                  className="w-full mt-4"
                  variant="outline"
                  onClick={() => navigate(`/builder/${site.id}`)}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Globe className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">
              {search ? 'Aucun résultat' : 'Aucun site pour le moment'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {search
                ? 'Essayez avec d\'autres termes de recherche'
                : 'Créez votre premier site web en quelques minutes'}
            </p>
            {!search && (
              <Button onClick={() => navigate('/templates')}>
                <Plus className="w-4 h-4 mr-2" />
                Créer un site
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer le site</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer "{siteToDelete?.name}" ? Cette action est
              irréversible et supprimera toutes les pages et le contenu associé.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
